import { type EvaluationDetails, type FlagUser, getFlags } from '@tix/flags'

import { environment, portal } from '@/helpers/Environment'

import { logInTrackJS } from '@/errors/helpers'

export type Flags = Partial<{
  // @see https://ticketure-team.atlassian.net/browse/TIC-4802
  hide_membership_links: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-4760
  address_autocomplete_for_uk_counties: boolean

  // TODO When we turn this on for everyone and remove this flag make sure to delete StripeWalletPayment and move its
  // paymentRequestButton() method logic into a helper. Also update mockStripePaymentRequest() to stop
  // mocking methods that no longer need to be.
  ignore_wallet_identity_data: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-4691
  tg_seat_accessibility: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-6544
  auth0_session_revocation: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-6750
  auth0_logout_redirect: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-6416
  ecomm_next_donations: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-6676
  bundles_list_page: boolean
}>

export type FlagIdentity = {
  id: string
  email?: string | null
}

const lastEmitted = new Map<string, string>()
let hasTrackedError = false

const evalOptions = {
  onFlagEvaluated: sendFlagToAnalytics,
}

export function evaluateFlags(identity?: FlagIdentity): Flags {
  const rules = environment.config_cat

  if (!rules) {
    // track missing embed, but not in jest environment
    if (window.tix) {
      trackOnce(new Error('ConfigCat embed missing'))
    }
    return {}
  }

  const resolvedIdentity = identity?.id ? { id: identity.id, email: identity.email ?? undefined } : undefined
  const flagUser: FlagUser = { tenant: portal.slug ?? '', ...resolvedIdentity }
  const result = getFlags(rules, flagUser, evalOptions)

  if (result.success) return result.flags as Flags

  trackOnce(result.error instanceof Error ? result.error : new Error(String(result.error)))
  return {}
}

function sendFlagToAnalytics(details: EvaluationDetails): void {
  if (!Array.isArray(window.dataLayer)) return

  const signature = `${details.key}:${String(details.value)}`
  if (lastEmitted.get(details.key) === signature) return
  lastEmitted.set(details.key, signature)

  if (!details.variationId) {
    logInTrackJS('ConfigCat flag is missing variationId', details)
  }

  window.dataLayer.push({
    event: 'configcat_flag_evaluated',
    exp_variant_string: `configcat-${details.key}-${details.value}`,
    variation_id: details.variationId ?? `${details.key}-${details.value}`,
  })
}

function trackOnce(error: Error): void {
  if (hasTrackedError) return
  hasTrackedError = true
  logInTrackJS('ConfigCat embed evaluation failed', { cause: error })
}
